import React, { useMemo, useState } from 'react';
import { motion } from "framer-motion";
import { Plus, FileCode, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";

const CodeFirstTab = ({ state }) => {
  const {
    entities,
    setEntities,
    migrations,
    setMigrations,
    setSqlOutput,
    setDbContextOutput,
    setCsharpOutput,
  } = state;

    const nextEntityToMigrate = useMemo(() => {
        return entities.find(e => !migrations.some(m => m.entityName === e.name)) || null;
    }, [entities, migrations]);

    const canGenerateMigration = !!nextEntityToMigrate;

    const [entityName, setEntityName] = useState("");
  const [fields, setFields] = useState([{ name: "Id", type: "int" }]);

  const toCSharpType = (t) => {
    const x = (t || "").toLowerCase();
    if (x.includes("int")) return "int";
    if (x.includes("bool")) return "bool";
    if (x.includes("datetime")) return "DateTime";
    if (x.includes("date")) return "DateOnly";
    return "string";
  };

  const toSqlType = (t) => {
    const x = (t || "").toLowerCase();
    if (x.includes("int")) return "int";
    if (x.includes("bool")) return "bit";
    if (x.includes("datetime")) return "datetime2";
    if (x.includes("date")) return "date";
    return "nvarchar(max)";
  };

  const buildEntityClass = (entity) => {
    const props = entity.fields
        .map((f) => `  public ${toCSharpType(f.type)} ${f.name} { get; set; }`)
        .join("\n");

    return `public class ${entity.name}\n{\n${props}\n}`;
  };

  const buildDbContext = (entitiesList) => {
    const dbSets = entitiesList
        .map((e) => `  public DbSet<${e.name}> ${e.name}s { get; set; }`)
        .join("\n");

    return (
        `public class ApplicationDbContext : DbContext\n` +
        `{\n${dbSets}\n\n` +
        `  protected override void OnModelCreating(ModelBuilder modelBuilder)\n` +
        `  {\n` +
        `    base.OnModelCreating(modelBuilder);\n` +
        `    // Fluent configuration goes here\n` +
        `  }\n` +
        `}`
    );
  };

  const buildMigrationFile = (migrationName, entity) => {
    const tableName = `${entity.name}s`; // naive plural for demo
    const cols = entity.fields
        .filter((f) => f.name)
        .map((f) => {
          const colType = toSqlType(f.type);
          const nullable = f.name === "Id" ? "nullable: false" : "nullable: true";
          return `          ${f.name} = table.Column<${toCSharpType(f.type)}>(${nullable})`;
        })
        .join(",\n");

    return (
        `using Microsoft.EntityFrameworkCore.Migrations;\n\n` +
        `#nullable disable\n\n` +
        `public partial class ${migrationName} : Migration\n` +
        `{\n` +
        `  protected override void Up(MigrationBuilder migrationBuilder)\n` +
        `  {\n` +
        `    migrationBuilder.CreateTable(\n` +
        `      name: "${tableName}",\n` +
        `      columns: table => new\n` +
        `      {\n${cols}\n` +
        `      },\n` +
        `      constraints: table =>\n` +
        `      {\n` +
        `        table.PrimaryKey("PK_${tableName}", x => x.Id);\n` +
        `      });\n` +
        `  }\n\n` +
        `  protected override void Down(MigrationBuilder migrationBuilder)\n` +
        `  {\n` +
        `    migrationBuilder.DropTable(\n` +
        `      name: "${tableName}");\n` +
        `  }\n` +
        `}\n`
    );
  };

  const addField = () => {
    setFields((prev) => [...prev, { name: "", type: "string" }]);
  };

  const updateField = (index, key, value) => {
    setFields((prev) => {
      const next = [...prev];
      next[index][key] = value;
      return next;
    });
  };

  const createEntity = () => {
    const name = entityName.trim();
    if (!name) {
      toast.error("Please enter an entity name");
      return;
    }

    const cleanedFields = fields.filter((f) => (f.name || "").trim());
    const newEntity = {
      name,
      fields: cleanedFields,
      createdAt: new Date().toISOString(),
    };

      setSqlOutput(`-- Entity "${name}" defined.\n-- Generate a migration to see the illustrative SQL.`);
      toast.success(`Entity "${name}" created`);

    setEntityName("");
    setFields([{ name: "Id", type: "int" }]);

    setEntities((prev) => {
      const next = [...prev, newEntity];
      setDbContextOutput(buildDbContext(next));
      return next;
    });
  };

    const buildCreateTableSqlFromEntity = (entity) => {
        const tableName = `${entity.name}s`;

        const lines = entity.fields
            .filter((f) => (f.name || "").trim())
            .map((f) => {
                const colName = f.name.trim();

                if (colName.toLowerCase() === "id") {
                    return `  [Id] INT IDENTITY(1,1) NOT NULL`;
                }

                // demo rule: *Id columns are NOT NULL (common for FKs), others NULL
                const isFkLike = colName.toLowerCase().endsWith("id");
                return `  [${colName}] ${toSqlType(f.type).toUpperCase()} ${isFkLike ? "NOT NULL" : "NULL"}`;
            });

        return (
            `-- Illustrative SQL (from migration)\n` +
            `CREATE TABLE [${tableName}] (\n` +
            `${lines.join(",\n")},\n` +
            `  CONSTRAINT [PK_${tableName}] PRIMARY KEY ([Id])\n` +
            `);\n`
        );
    };

    const generateMigration = () => {
        if (!nextEntityToMigrate) {
            toast.info("No new entities detected — migration is already up to date.");
            return;
        }

        const migrationName = `Add${nextEntityToMigrate.name}sTable`;
        const content = buildMigrationFile(migrationName, nextEntityToMigrate);

        setSqlOutput(buildCreateTableSqlFromEntity(nextEntityToMigrate));

        const newMigration = {
            id: Date.now(),
            name: migrationName,
            timestamp: new Date().toISOString(),
            status: "pending",
            content,
            entityName: nextEntityToMigrate.name
        };

        setMigrations((prev) => [...prev, newMigration]);

        setCsharpOutput(
            `// EF Core CLI\n` +
            `dotnet ef migrations add ${migrationName}\n` +
            `dotnet ef database update\n`
        );

        toast.success(`Migration "${migrationName}" generated`);
    };

    return (
      <div className="space-y-6 pt-4">
        <div className="space-y-4">
          <div>
            <h3 className="mb-2 text-sm font-semibold">Code-First Development</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Define entities as C# classes</li>
              <li>• Configure properties and relationships</li>
              <li>• Generate migrations from model changes</li>
            </ul>
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="entity-name">Entity Name</Label>
              <Input
                  id="entity-name"
                  placeholder="e.g., Student, Course"
                  value={entityName}
                  onChange={(e) => setEntityName(e.target.value)}
              />
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Properties</Label>
                <Button size="sm" variant="outline" onClick={addField}>
                  <Plus className="mr-1 h-3 w-3" />
                  Add Field
                </Button>
              </div>

              <div className="space-y-2">
                {fields.map((field, index) => (
                    <div key={index} className="grid grid-cols-2 gap-2">
                      <Input
                          placeholder="Name"
                          value={field.name}
                          onChange={(e) => updateField(index, "name", e.target.value)}
                      />
                      <Input
                          placeholder="Type"
                          value={field.type}
                          onChange={(e) => updateField(index, "type", e.target.value)}
                      />
                    </div>
                ))}
              </div>
            </div>

            <Button className="w-full" onClick={createEntity}>
              Create Entity
            </Button>
          </div>
        </div>

        {/* Entities */}
        {entities.length > 0 && (
            <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-2"
            >
              <h4 className="text-sm font-semibold">Created Entities</h4>
              <div className="space-y-2">
                {entities.map((entity, index) => (
                    <Dialog key={index}>
                      <DialogTrigger asChild>
                        <Card className="cursor-pointer bg-surface-elevated hover:border-primary/40 border border-border">
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="font-medium">{entity.name}</p>
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {entity.fields.map((field, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs">
                                        {field.name}: {field.type}
                                      </Badge>
                                  ))}
                                </div>
                              </div>
                              <FileCode className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </CardContent>
                        </Card>
                      </DialogTrigger>

                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>{entity.name} Model (Example)</DialogTitle>
                          <DialogDescription>
                            Demo representation of how the model could look in C#.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="rounded-lg border border-border bg-surface-elevated p-4">
                    <pre className="overflow-x-auto text-sm leading-relaxed">
                      <code className="text-foreground">{buildEntityClass(entity)}</code>
                    </pre>
                        </div>
                      </DialogContent>
                    </Dialog>
                ))}
              </div>
                <Button
                    variant="secondary"
                    className="w-full"
                    onClick={generateMigration}
                    disabled={!canGenerateMigration}
                >
                    {canGenerateMigration
                        ? `Generate Migration (${nextEntityToMigrate.name})`
                        : "Migration Up-to-date"}
                </Button>
            </motion.div>
        )}

        {/* Migrations + modal */}
        {migrations.length > 0 && (
            <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="space-y-2"
            >
              <h4 className="text-sm font-semibold">Migrations</h4>

              <div className="space-y-1">
                {migrations.map((migration) => (
                    <Dialog key={migration.id}>
                      <DialogTrigger asChild>
                        <div className="flex cursor-pointer items-center justify-between rounded-md border border-border bg-surface-elevated px-3 py-2 text-sm hover:border-primary/40">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      {migration.name}
                    </span>
                          <Badge variant="secondary">View File</Badge>
                        </div>
                      </DialogTrigger>

                      <DialogContent className="max-w-3xl">
                        <DialogHeader>
                          <DialogTitle>{migration.name}.cs</DialogTitle>
                          <DialogDescription>
                            Demo migration file content for {migration.entityName}.
                          </DialogDescription>
                        </DialogHeader>

                        <div className="rounded-lg border border-border bg-surface-elevated p-4">
                          <p className="mb-2 text-xs font-medium text-muted-foreground">
                            EF CORE COMMANDS
                          </p>
                          <pre className="overflow-x-auto text-sm leading-relaxed">
                      <code className="text-foreground">
                        {`dotnet ef migrations add ${migration.name}\n` +
                            `dotnet ef database update`}
                      </code>
                    </pre>
                        </div>

                        <div className="rounded-lg border border-border bg-surface-elevated p-4">
                          <p className="mb-2 text-xs font-medium text-muted-foreground">
                            MIGRATION FILE (EXAMPLE)
                          </p>
                          <pre className="overflow-x-auto text-sm leading-relaxed">
                      <code className="text-foreground">{migration.content}</code>
                    </pre>
                        </div>
                      </DialogContent>
                    </Dialog>
                ))}
              </div>
            </motion.div>
        )}
      </div>
  );
};

export default CodeFirstTab;
